import { type Modal } from "shared/Modal";
import { generateId } from "shared/functions";
import { Signal } from "shared/Signal";

export type HeaderTag = "h1"|"h2"|"h3"|"h4"|"h5"|"h6";

interface DialogOptions<T> {
	/** The invoking element. This is currently only used for popup animations, but could be used to infer the title tag later. */
	sourceElement: HTMLElement;
	/** A Signal used to dismiss the dialog. */
	dismissSignal: Signal<T>;
	/** The width of the element */
	width: number;
	/** The HTML tagName of the title element. Should be contextual per the location of the invoking control */
	titleTag: HeaderTag;
	/** Prevents the title from displaying */
	hideTitle?: boolean;
	/** Add a separator under the title */
	headerSeparator: boolean;
	disableBodyFormatting?: boolean;
	/** An Icon to place alongside the title */
	titleIcon?: HTMLElement;
	/** When no icon provided, a class for an icon to place alongside the title */
	titleIconClass?: string;
}

export interface ShowDialogOptions<T> extends Partial<DialogOptions<T>> {
	/** The title of the dialog. When passed as a string, will escape HTML */
	title: Node|string;
	/** The body of the dialog. May contain messages or controls */
	body: Node|string;
	/** Any controls that need to be displayed at the footer of the dialog. */
	actions?: HTMLElement[];
	/** A signal used to dismiss the dialog */
	dismissSignal: Signal<T>;
	/** What should happen when the form is submitted */
	defaultAction: (e: Event) => void;
	dismissable?: boolean;
}

export interface PromptDialogOptions extends DialogOptions<string|null> {
	/** When no `input` parameter is provided, the input is created with this value as a max length. */
	maxLength: number;
	/** The value of the input. */
	value: string;
	label: string;
	/** If desired, allows the prompt to get its value from an alternate source, such as a select.  */
	input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
}

export class Dialog {
	constructor(private modal: Modal) { }

	public static makeButton(content: string|Node, callback?: (e: MouseEvent) => void): HTMLButtonElement {
		const button = document.createElement("button");
		button.classList.add("button", "-wide", callback ? "-secondary" : "-primary");
		if (callback) {
			button.setAttribute("type", "button");
			button.addEventListener("click", callback);
		}
		button.append(content);
		return button;
	}

	private static makeInput(maxLength?: number): HTMLInputElement {
		const input = document.createElement("input");
		input.classList.add("input", "form_textInput");
		input.type = "text";
		if (maxLength) {
			input.maxLength = maxLength;
		}
		return input;
	}

	public static makeSelect(options: Array<{ value: string, label: string }>, value?: string): HTMLSelectElement {
		const select = document.createElement("select");
		select.classList.add("select");
		select.append(...options.map(o => {
			const option = document.createElement("option");
			option.value = o.value;
			option.innerText = o.label;
			return option;
		}));
		if (value) {
			select.value = value;
		}
		return select;
	}

	public show<T>(params: ShowDialogOptions<T>): Promise<T|null> {
		const titleId = generateId("dialog-");

		const element = document.createElement("div");

		element.classList.add("card", "-dialog", "-modal");
		element.setAttribute("role", "dialog");
		element.setAttribute("aria-modal", "true");
		element.setAttribute("aria-labelledby", titleId);

		const form = document.createElement("form");
		params.dismissSignal.listen(value => this.modal.back(value));
		form.addEventListener("submit", e => params.defaultAction(e));
		const header = document.createElement("div");
		header.classList.add("card_header", "flex", "-row", "-gutter");
		if (params.headerSeparator) {
			header.classList.add("-dialog-separator");
		}

		let titleIcon: HTMLElement|undefined = params.titleIcon;
		if (!titleIcon && params.titleIconClass) {
			titleIcon = document.createElement("i");
			titleIcon.className = params.titleIconClass;
		}
		if (titleIcon) {
			titleIcon.classList.add("flex_item");
			header.append(titleIcon);
		}

		const title = document.createElement(params.titleTag || "h2");
		title.id = titleId;
		title.classList.add("card_title", "flex_item", "-grow");
		if (params.hideTitle) {
			header.classList.add("g_screenReader");
		}
		title.append(params.title);
		header.append(title);
		form.append(header);

		if (params.body) {
			const body = document.createElement("div");
			if (!params.disableBodyFormatting) {
				body.classList.add("card_body");
			}
			body.append(params.body);
			form.append(body);
		}

		if (params.actions) {
			const footer = document.createElement("div");
			footer.classList.add("card_footer", "-actions");
			footer.append(...params.actions);
			form.append(footer);
		}

		element.append(form);
		return this.modal.show<T>(element, {
			width:       params.width ?? 400,
			dismissable: params.dismissable ?? true,
			source:      params.sourceElement
		});
	}

	public alert(title: string|Node, body: string|Node, confirm: string, options: Partial<DialogOptions<null>> = { }): Promise<null> {
		const dismissSignal = options.dismissSignal || new Signal();
		return this.show<null>({
			title,
			body,
			width:           400,
			headerSeparator: false,
			...options,
			actions:         [Dialog.makeButton(confirm)],
			dismissSignal,
			defaultAction:   (e) => {
				e.preventDefault();
				dismissSignal.send(null);
			}
		});
	}

	public prompt(title: string|Node, body: string|Node, confirm: string, decline: string, options: Partial<PromptDialogOptions> = { }): Promise<string|null> {
		const dismissSignal = options.dismissSignal || new Signal();
		const bodyWrapper = document.createDocumentFragment();
		const declineButton = Dialog.makeButton(decline, () => dismissSignal.send(null));
		const confirmButton = Dialog.makeButton(confirm);
		const input = options.input || Dialog.makeInput(options.maxLength);
		const container = document.createElement("div");

		if (options.value) {
			input.value = options.value;
		}
		if (options.label) {
			const label = document.createElement("label");
			if (!input.id) {
				input.id = generateId("input-");
			}
			label.setAttribute("for", input.id);
			label.classList.add("form_label");
			label.innerText = options.label;
			container.append(label);
		}
		container.append(input);
		input.style.width = "100%";
		bodyWrapper.append(body, container);
		const checkDisabled = () => {
			confirmButton.classList.toggle("-disabled", !input.value);
			confirmButton.setAttribute("aria-disabled", input.value ? "false" : "true");
		};
		input.addEventListener("input", checkDisabled);
		input.addEventListener("change", checkDisabled);
		checkDisabled();

		return this.show<null|string>({
			headerSeparator: false,
			...options,
			title,
			body:            bodyWrapper,
			actions:         [ declineButton, confirmButton ],
			dismissSignal,
			defaultAction:   (e) => {
				e.preventDefault();
				if (input.value) {
					dismissSignal.send(input.value);
				}
			}
		});
	}

	public async confirm(title: string|Node, body: string|Node, confirm: string, decline: string, options: Partial<DialogOptions<boolean>> = { }): Promise<boolean> {
		const dismissSignal = options.dismissSignal || new Signal();
		return Boolean(await this.show<boolean>({
			headerSeparator: false,
			...options,
			title,
			body,
			actions:         [
				Dialog.makeButton(decline, () => dismissSignal.send(false)),
				Dialog.makeButton(confirm)
			],
			dismissSignal,
			defaultAction: (e) => {
				e.preventDefault();
				dismissSignal.send(true);
			}
		}));
	}
}
